import React, { useEffect } from 'react';

import KStatus from '../base/data/KStatus';
import DataStore from '../base/plumbing/DataStore';
import C from '../C';
import ActionMan from '../plumbing/ActionMan';

import Misc from '../base/components/Misc';
import { getDataItem, setWindowTitle } from '../base/plumbing/Crud';
import ListLoad from '../base/components/ListLoad';
import PropControl from '../base/components/PropControl';
import { MondayLink, MondayLinkOut } from './monday';
import SubCard from '../base/components/SubCard';
import SavePublishDeleteEtc from '../base/components/SavePublishDeleteEtc';
import AThing from '../base/data/AThing';
import PropControlDataItem from '../base/components/propcontrols/PropControlDataItem';
import Money from '../base/data/Money';
import ImpactEditor from './ImpactEditor';
import { KImpactStage } from '../base/data/Impact';
import LinkOut from '../base/components/LinkOut';
import { getBillUrl, getInvoiceUrl } from '../data/Xero';
import Icon from '../base/components/Icon';
import { getUrlVars } from '../base/utils/miscutils';
import SearchQuery from '../base/searchquery';


const type = C.TYPES.ImpactDebit;

/**
 * Provide single-tag and whole-campaign tables for Green Ad Tags.
 * Shows impression counts & data-transfer totals with various single and cross breakdowns.
 * TODO Campaign and tag mode ended up almost identical - abstract them into a single component.
 * @returns 
 */
function ImpactDebitPage() {
	const blocked = Misc.CheckAccess({ can: 'editImpact' });
	if (blocked) return blocked;

	const path = DataStore.getValue(['location', 'path']);
	const itemId = path[1];

	if (!itemId) return <ListImpactDebits />;
	return <EditImpactDebit itemId={itemId} />;
}

function ListImpactDebits() {
	setWindowTitle(type);

	let { idtype, q } = getUrlVars();
	if (idtype === 'roots') {
		q = SearchQuery.setProp(q, 'parent', 'unset');
	} else if (idtype === 'suppliers') {
		q = SearchQuery.setProp(q, 'stage', 'SUPPLIER');
	}

	return <>
		<PropControl type="select" prop="idtype"
			options={['all', 'roots', 'suppliers']}
			labels={['All', 'Roots (no child objects)', 'Suppliers (not actual impacts)']}
			label="Type of ImpactDebit record"
		/>
		<ListLoad q={q} canCreate canFilter type={type} status={KStatus.ALL_BAR_TRASH} ListItem={ImpactDebitListItem} />
	</>;
}


const MONDAY_BOARD = "2613874238";
const MONDAY_DEALS_BOARD = "2643467896";


function EditImpactDebit({ itemId }) {
	const pvItem = ActionMan.getDataItem({ type, id: itemId, status: KStatus.DRAFT });
	const item = pvItem.value;

	useEffect(() => {
		if (!item) return;
		// Fill in job number and name from campaign, if necessary / available
		const pvCampaign = item.campaign && getDataItem({ type: C.TYPES.Campaign, id: item.campaign, status: KStatus.PUBLISHED, swallow: true });
		pvCampaign?.promise.then(campaign => {
			if (!item.jobNumber && campaign?.jobNumber) {
				item.jobNumber = campaign?.jobNumber;
			}
			if (!item.name) {
				item.name = [campaign?.name, item.impact?.n, item.impact?.name].filter(x => !!x).join(' ');
			}
		});
	}, [item, item?.campaign]);

	if (!item) {
		setWindowTitle(type);
		return <>
			<h1>Impact Debit: {itemId}</h1>
			{pvItem.resolved ? (
				<Misc.Loading />
			) : (
				<p>No ImpactDebit could be found for this ID.</p>
			)}
		</>;
	}

	setWindowTitle(item);

	const path = DataStore.getDataPath({ status: KStatus.DRAFT, type, id: item.id });
	const ipath = path.concat('impact');

	return <>
		<h1>Impact Debit Editor</h1>
		ID: {item.id}
		<SubCard title="Impact">
			<PropControl type="text" prop="name" path={path} label="Display name for this impact project"
				help="This is a human-friendly name. It is not the canonical name."
			/>
			<ImpactEditor ipath={ipath} stage={item.stage} />
		</SubCard>

		<SubCard title="Audit Trail">
			<MondayLink label="Monday.com Donations ID" board={MONDAY_BOARD} item={item} path={path} type={type} canEdit />

			<MondayLink prop="crmDeal" label="Monday.com Deal ID" board={MONDAY_DEALS_BOARD} item={item} path={path} type="Deal" canEdit />

			<PropControlDataItem label="Matching Impact Credit" prop="creditId" path={path} itemType={C.TYPES.ImpactCredit} />

			<PropControlDataItem label prop="parent" path={path} itemType={C.TYPES.ImpactDebit}
				help="Use-cases: for splitting a Green Media agency-level offset out to brands, or splitting a T4G earnings 'parent' debit out to charities."
			/>

			<PropControl prop="slice" label path={path} type="number" help="If this is a slice of a larger impact, then number it."
				disabled={!item.parent}
			/>

			<PropControlDataItem label prop="campaign" path={path} itemType={C.TYPES.Campaign} />
			
			<PropControl label prop="product" path={path} type="select" options={['T4G','WTD','Green']} />

			<PropControl label prop="start" path={path} type="date" />

			<PropControl label prop="end" path={path} type="date" />

			<PropControlDataItem label="Agency" prop="agencyId" path={path} itemType={C.TYPES.Agency} />

			<PropControlDataItem label="Advertiser" prop="vertiser" path={path} itemType={C.TYPES.Advertiser} />

			<PropControl label="Job number" prop="jobNumber" path={path} />

			<PropControl label="Xero ID for Donation/Offset" path={path} prop="xero"
				append={item.xero && <LinkOut className="input-group-text p-0" href={getBillUrl(item.xero)}><Icon name="link" /></LinkOut>}
			/>

			<PropControl label="Xero ID for Invoice to Client" path={path} prop="xeroInvoice"
				append={item.xeroInvoice && <LinkOut className="input-group-text p-0" href={getInvoiceUrl(item.xeroInvoice)}><Icon name="link" /></LinkOut>}
			/>

			<PropControl label="Donation ID" prop="donationId" path={path} />

			<PropControl label prop="stage" path={path} type="select" options={KImpactStage.values} />
		</SubCard>

		<SavePublishDeleteEtc type={C.TYPES.ImpactDebit} id={item.id} canDiscard position="relative" />
	</>;
}


const impactDebitListName = (item) => {
	const pvBrand = item.vertiser && getDataItem({type: C.TYPES.Advertiser, id: item.vertiser, status: KStatus.PUBLISHED, swallow: true});
	return [
		item.name,
		item.impact?.charity,
		item.impact?.amount && Money.str(item.impact?.amount),
		item.impact?.n,
		item.impact?.name,
		pvBrand?.value?.name
	].filter(x => !!x).join(' / ');
};


export function ImpactDebitListItem({ item }) {
	const id = item.id;
	const imgUrl = item.impact?.img?.contentUrl || item.impact?.charity?.logo;
	let name = impactDebitListName(item);
	if (name.length > 280) name = name.slice(0, 280);
	const status = item.status || '';

	return (
		<div className="info text-left">
			{imgUrl && <Misc.ImgThumbnail url={imgUrl} className="float-right" />}
			<div className="name">{name}</div>
			<MondayLinkOut board={MONDAY_BOARD} item={item} />
			<div className="detail small">
				id: <span className="id">{id}</span>{' '}
				<span className="status">{status.toLowerCase()}</span>{' '}
				Created: <Misc.RoughDate date={item.created} />
				{AThing.lastModified(item) && <> Modified: <Misc.RoughDate date={AThing.lastModified(item)} /></>}
			</div>
		</div>
	);
}

export default ImpactDebitPage;
